import {
  createContext, useContext, useEffect, useState, useMemo, useCallback,
} from "react";
import { api } from "./api";
import { useAuth } from "./auth";

/**
 * Global "active Bling" state.
 *
 * Every page that reads/writes Bling data (Faturamento, Catálogo, Regras,
 * Preço, Dashboard) subscribes to this context via ``useActiveBling()``.
 * When the user picks a different Bling in the sidebar selector, every
 * subscribed page auto-refetches with the new ``integration_id``.
 *
 * Persistence: the choice survives page reloads via localStorage under
 * ``vyntric.activeBlingId``.
 */

const KEY = "vyntric.activeBlingId";
const BlingContext = createContext(null);


export function BlingProvider({ children }) {
  const { user } = useAuth();
  const [blings, setBlings] = useState([]);
  const [activeId, setActiveIdState] = useState(() => localStorage.getItem(KEY) || "");
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);

  // Load the tenant's Bling integrations whenever the user changes.
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setBlings([]);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/integrations");
        const connected = (data || [])
          .filter((i) => i.type === "bling" && i.status === "connected");
        if (cancelled) return;
        setBlings(connected);
        // If the persisted choice no longer matches any Bling, fall back
        // to the first connected one so the app is never stuck without
        // a valid target.
        const persisted = localStorage.getItem(KEY);
        const stillValid = connected.some((b) => b.id === persisted);
        if (!stillValid && connected.length > 0) {
          setActiveIdState(connected[0].id);
          localStorage.setItem(KEY, connected[0].id);
        }
      } catch (e) {
        if (!cancelled) setBlings([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, reloadTick]);

  const setActiveId = useCallback((id) => {
    setActiveIdState(id || "");
    if (id) localStorage.setItem(KEY, id);
    else localStorage.removeItem(KEY);
  }, []);

  const activeBling = useMemo(
    () => blings.find((b) => b.id === activeId) || blings[0] || null,
    [blings, activeId],
  );

  const refresh = useCallback(() => setReloadTick((t) => t + 1), []);

  // Listen to a custom refresh event so unrelated components (e.g.
  // Integrations page renaming a Bling) can trigger a reload without
  // needing to import the context hook.
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("vyntric:blings-changed", handler);
    return () => window.removeEventListener("vyntric:blings-changed", handler);
  }, [refresh]);

  const value = useMemo(() => ({
    blings,
    activeBling,
    activeId: activeBling?.id || "",
    setActiveId,
    loading,
    refresh,
    // Helper to append integration_id automatically to API calls.
    withIntegrationId: (params = {}) => activeBling
      ? { ...params, integration_id: activeBling.id }
      : params,
  }), [blings, activeBling, setActiveId, loading, refresh]);

  return (
    <BlingContext.Provider value={value}>{children}</BlingContext.Provider>
  );
}


export function useActiveBling() {
  const ctx = useContext(BlingContext);
  if (!ctx) {
    // Called outside provider — return a no-op fallback so pages don't
    // crash during hot-reload or in isolated tests.
    return {
      blings: [], activeBling: null, activeId: "", setActiveId: () => {},
      loading: false, refresh: () => {},
      withIntegrationId: (p = {}) => p,
    };
  }
  return ctx;
}


/**
 * Prefer the nickname (user-defined label). Falls back to name, then id.
 * Used everywhere we render a Bling's label in the UI.
 */
export function blingLabel(bling) {
  if (!bling) return "";
  return (bling.nickname && String(bling.nickname).trim())
    || (bling.name && String(bling.name).trim())
    || `Bling #${String(bling.id || "").slice(0, 6)}`;
}
